import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { LuChevronDown, LuClipboardList } from "react-icons/lu";
import { Select } from "components/Select";
import { Tag } from "components/Tag/TagChip";
import { TagColor, TagSize } from "components/Tag";
import {
  ApiError,
  CRMBoardMoveResponse,
  CRMVisualizationColumnResponse,
  CRMVisualizationResponse,
  OrganizationDataKeyResponse,
  OrganizationResponseForAdmins,
  VisualizationsService,
  Auth0AccountRole,
} from "client/openapi";
import {
  DndContext,
  DragEndEvent,
  DragOverlay,
  DragStartEvent,
  MouseSensor,
  TouchSensor,
  pointerWithin,
  rectIntersection,
  CollisionDetection,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import EditVisualizationDialog from "./EditVisualizationDialog";
import DeleteVisualizationDialog from "./DeleteVisualizationDialog";
import CreateVisualizationDialog from "./CreateVisualizationDialog";
import AddOrEditColumnDialog from "./AddOrEditColumnDialog";
import ColumnActionsMenu from "./ColumnActionsMenu";
import MoveFieldsDialog, { MoveAsk } from "./MoveFieldsDialog";
import {
  CardDragData,
  ColumnDropData,
  DraggableCard,
  DroppableColumn,
  cardKey,
} from "./BoardDnd";
import useBoardChips from "./CardChips";
import useCardTask from "./CardTask";
import "./index.css";
import { PageStatus } from "types/pageStatus.interface";
import { ErrorBlock } from "components/StatusBlock";
import { Button, ButtonColor, ButtonShape } from "components/Button";
import { Dialog, DialogContent, DialogTrigger } from "components/Dialog";
import AdministrativeInfo from "pages/manage/students/[id]/StudentInfoTab/AdministrativeInfo";
import Notifications from "util/notifications";
import { errorText } from "components/Forms/formsApi";
import TadpoleBadge from "components/TadpoleBadge";
import { FormOption, SubjectOption } from "../search/editor";

// 1) Import your helper functions
import {
  addColumnToVisualization,
  updateColumnInVisualization,
  deleteColumnFromVisualization,
} from "./visualizationUtils"; // <-- your helper file path

const CARD_COLORS: Record<string, TagColor> = {
  [Auth0AccountRole.ME]: TagColor.PURPLE,
  [Auth0AccountRole.PARENT]: TagColor.BLUE,
  [Auth0AccountRole.ORG_TUTOR]: TagColor.GOLD,
};

// A drop lands on the column under the pointer; if the pointer is between
// columns, the column the card overlaps most is used.
const collisionDetection: CollisionDetection = (args) => {
  const underPointer = pointerWithin(args);
  return underPointer.length > 0 ? underPointer : rectIntersection(args);
};

type BoardPerson = {
  id: number;
  org_id: number;
  first_name: string;
  last_name: string;
};

/** A board in the list; built-in boards carry Tadpole's "Tt" badge. */
function boardOption(board: CRMVisualizationResponse) {
  return {
    value: board.id.toString(),
    label: board.name,
    builtIn: !!board.built_in,
  };
}

function BoardOptionLabel(option: ReturnType<typeof boardOption>) {
  return (
    // The badge at the end of the row (left of the list's checkmark).
    <span className="flex w-full flex-1 items-center justify-between gap-2">
      <span className="truncate">{option.label}</span>
      {option.builtIn && <TadpoleBadge />}
    </span>
  );
}

export default function VisualizationView({
  orgId,
  organization,
  organizationName,
  studentKeys,
  parentKeys,
  tutorKeys,
  subjects,
  forms,
}: {
  orgId: number;
  organization: OrganizationResponseForAdmins;
  organizationName: string;
  studentKeys: OrganizationDataKeyResponse[];
  parentKeys: OrganizationDataKeyResponse[];
  tutorKeys: OrganizationDataKeyResponse[];
  subjects: SubjectOption[];
  forms: FormOption[];
}) {
  const [visualizations, setVisualizations] = useState<
    CRMVisualizationResponse[]
  >([]);
  const [selectedVisualization, setSelectedVisualization] = useState<
    CRMVisualizationResponse | undefined
  >(undefined);
  const [isLoadingVisualizations, setIsLoadingVisualizations] = useState(true);
  const [exportingData, setExportingData] = useState(false);

  const [addColumnDialogOpen, setAddColumnDialogOpen] = useState(false);

  // Drag and drop
  const [dragging, setDragging] = useState<CardDragData | null>(null);
  // One move at a time: while it saves, its card is faded and no card moves.
  const [savingKey, setSavingKey] = useState<string | null>(null);
  const [moveAsk, setMoveAsk] = useState<MoveAsk | null>(null);
  const chips = useBoardChips(orgId, organizationName, selectedVisualization);
  const cardTask = useCardTask(organization, chips.reload);

  // Column names wrap in full across the header, with Actions under them.
  // Every name takes the tallest one's height, so the Actions buttons line up
  // and the cards start in line across columns.
  const titles = useRef(new Map<number, HTMLElement>());
  const [namePx, setNamePx] = useState<number>();
  useLayoutEffect(() => {
    const measure = () =>
      setNamePx(
        Math.max(
          0,
          ...Array.from(titles.current.values(), (el) => el.offsetHeight)
        ) || undefined
      );
    measure();
    const observer = new ResizeObserver(measure);
    titles.current.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, [selectedVisualization?.columns]);

  const sensors = useSensors(
    // A press that moves less than 8px is a click, not a drag.
    useSensor(MouseSensor, { activationConstraint: { distance: 8 } }),
    // On touch screens a long press picks the card up; a swipe still scrolls.
    useSensor(TouchSensor, {
      activationConstraint: { delay: 250, tolerance: 5 },
    })
  );

  const fetchVisualizations = async () => {
    try {
      setIsLoadingVisualizations(true);
      const data = await VisualizationsService.getVisualizations({ orgId });
      setVisualizations(data);
      if (data.length > 0) {
        setSelectedVisualization(data[0]);
      }
      setIsLoadingVisualizations(false);
    } catch (error) {
      console.error("Error fetching visualizations:", error);
      setIsLoadingVisualizations(false);
    }
  };

  useEffect(() => {
    fetchVisualizations();
  }, [orgId]);

  const handleVisualizationCreated = (
    newVisualization: CRMVisualizationResponse
  ) => {
    setVisualizations((prev) => [...prev, newVisualization]);
    setSelectedVisualization(newVisualization);
  };

  const handleVisualizationUpdate = (updated: CRMVisualizationResponse) => {
    setVisualizations((prevVisualizations) =>
      prevVisualizations.map((v) => (v.id === updated.id ? updated : v))
    );
    setSelectedVisualization(updated);
  };

  const handleVisualizationDelete = () => {
    if (!selectedVisualization) return;
    setVisualizations((prevVisualizations) =>
      prevVisualizations.filter((v) => v.id !== selectedVisualization.id)
    );
    setSelectedVisualization(undefined);
  };

  const handleColumnAdded = (newColumn: CRMVisualizationColumnResponse) => {
    setSelectedVisualization((prev) => {
      if (!prev) return prev;
      return addColumnToVisualization(prev, newColumn);
    });
    setAddColumnDialogOpen(false);
  };

  const handleColumnUpdated = (
    updatedColumn: CRMVisualizationColumnResponse
  ) => {
    setSelectedVisualization((prev) => {
      if (!prev) return prev;
      return updateColumnInVisualization(prev, updatedColumn);
    });
  };

  const handleColumnDeleted = (deletedColumnId: number) => {
    setSelectedVisualization((prev) => {
      if (!prev) return prev;
      return deleteColumnFromVisualization(prev, deletedColumnId);
    });
  };

  /** An archived person leaves every board at once: no board shows archived people. */
  const removeFromBoards = (personType: Auth0AccountRole, personId: number) => {
    const leave = <T extends { id: number }>(
      kind: Auth0AccountRole,
      people?: T[] | null
    ) =>
      kind === personType ? people?.filter((p) => p.id !== personId) : people;
    const without = (
      board: CRMVisualizationResponse
    ): CRMVisualizationResponse => ({
      ...board,
      columns: board.columns.map((column) => ({
        ...column,
        students_in_column: leave(
          Auth0AccountRole.ME,
          column.students_in_column
        ),
        parents_in_column: leave(
          Auth0AccountRole.PARENT,
          column.parents_in_column
        ),
        tutors_in_column: leave(
          Auth0AccountRole.ORG_TUTOR,
          column.tutors_in_column
        ),
      })),
    });
    setVisualizations((prev) => prev.map(without));
    setSelectedVisualization((prev) => prev && without(prev));
  };

  /** Show the server's columns for a board (ignored if another board is open). */
  const applyColumns = (
    visualizationId: number,
    columns: CRMVisualizationColumnResponse[]
  ) => {
    setVisualizations((prev) =>
      prev.map((v) => (v.id === visualizationId ? { ...v, columns } : v))
    );
    setSelectedVisualization((prev) =>
      prev && prev.id === visualizationId ? { ...prev, columns } : prev
    );
  };

  const reloadBoard = (visualizationId: number) => {
    // A built-in board (negative id) has no row; it comes with the list.
    (visualizationId < 0
      ? VisualizationsService.getVisualizations({ orgId }).then(
          (all) => all.find((v) => v.id === visualizationId)?.columns ?? []
        )
      : VisualizationsService.getVisualizationColumns({ visualizationId })
    )
      .then((columns) => applyColumns(visualizationId, columns))
      .catch(() =>
        Notifications.error(
          "The board could not be reloaded. Refresh the page."
        )
      );
  };

  const handleDragStart = (event: DragStartEvent) => {
    setDragging((event.active.data.current as CardDragData) || null);
  };

  const handleDragEnd = (event: DragEndEvent) => {
    setDragging(null);

    const card = event.active.data.current as CardDragData | undefined;
    const target = event.over?.data.current as ColumnDropData | undefined;
    const board = selectedVisualization;
    if (!card || !target || !board) return;
    if (target.columnId === card.fromColumnId) return;
    if (target.columnType !== card.personType) return;

    const column = board.columns.find((c) => c.id === target.columnId);
    if (!column) return;

    setSavingKey(cardKey(card.personType, card.personId));
    VisualizationsService.moveBoardCard({
      visualizationId: board.id,
      requestBody: {
        person_type: card.personType,
        person_id: card.personId,
        to_column_id: target.columnId,
      },
    })
      .then((response) => {
        if (response.status === CRMBoardMoveResponse.status.NEEDS_INPUT) {
          setMoveAsk({
            visualizationId: board.id,
            column,
            personType: card.personType,
            personId: card.personId,
            name: card.label,
            fields: response.fields || [],
          });
          return;
        }
        if (response.columns) applyColumns(board.id, response.columns);
        if (response.status === CRMBoardMoveResponse.status.NOT_MOVED) {
          Notifications.error(
            response.message || `${card.label} could not be moved.`
          );
        }
      })
      .catch((e: ApiError) => {
        Notifications.error(
          errorText(e, `${card.label} could not be moved. Nothing was changed.`)
        );
        reloadBoard(board.id);
      })
      .finally(() => setSavingKey(null));
  };

  const renderCard = (
    person: BoardPerson,
    personType: Auth0AccountRole,
    column: CRMVisualizationColumnResponse
  ) => {
    const label = `${person.first_name} ${person.last_name}`;
    const key = cardKey(personType, person.id);
    return (
      <Dialog key={key}>
        <DraggableCard
          id={`${column.id}:${key}`}
          data={{
            personType,
            personId: person.id,
            fromColumnId: column.id,
            label,
          }}
          saving={savingKey === key}
          locked={savingKey !== null}
        >
          <Tag
            item={label}
            color={CARD_COLORS[personType]}
            size={TagSize.MEDIUM}
          >
            <span className="board-card-actions">
              {cardTask.button(personType, person.id, label)}
              {/* Only the clipboard opens the person's data; a press on it never drags. */}
              <DialogTrigger asChild>
                <button
                  type="button"
                  className="board-card-open"
                  aria-label={`Open ${label}'s data`}
                  title={`Open ${label}'s data`}
                  onMouseDown={(e) => e.stopPropagation()}
                  onTouchStart={(e) => e.stopPropagation()}
                >
                  <LuClipboardList aria-hidden strokeWidth={2.5} />
                </button>
              </DialogTrigger>
            </span>
            {chips.card(personType, person.id)}
          </Tag>
        </DraggableCard>
        <DialogContent>
          <div className="column-admin-info-dialog-content">
            <AdministrativeInfo
              exportingData={exportingData}
              setExportingData={setExportingData}
              userType={personType}
              student={
                personType === Auth0AccountRole.ME ? (person as any) : undefined
              }
              parent={
                personType === Auth0AccountRole.PARENT
                  ? (person as any)
                  : undefined
              }
              tutor={
                personType === Auth0AccountRole.ORG_TUTOR ? person : undefined
              }
              organization_id={orgId}
              showViewProfileButton={true}
              onArchived={() => removeFromBoards(personType, person.id)}
            />
          </div>
        </DialogContent>
      </Dialog>
    );
  };

  if (isLoadingVisualizations) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-900"></div>
      </div>
    );
  }

  if (visualizations.length === 0) {
    return (
      <div className="visualization-container">
        <div
          className="mx-auto w-full"
          style={{
            display: "flex",
            backgroundColor: "#f8f8f8",
            borderRadius: "0.75rem",
            alignItems: "center",
            padding: "5rem 1.5rem",
            textAlign: "center",
          }}
        >
          <div className="flex flex-col items-center justify-center w-full">
            <ErrorBlock
              status={PageStatus.ERROR}
              title="No visualizations"
              message="You have not created any visualizations yet."
            />
            <CreateVisualizationDialog
              orgId={orgId}
              onVisualizationCreated={handleVisualizationCreated}
            />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="visualization-container">
      <div className="visualization-selector mb-6 flex flex-col sm:flex-row gap-2">
        <div className="flex flex-col sm:flex-row gap-2">
          <div style={{ minWidth: "200px" }}>
            <Select
              // Its menu fits the longer board names (index.css).
              classNamePrefix="crm-board-picker"
              options={visualizations
                .filter((v) => v != null && typeof v.id !== "undefined")
                .map(boardOption)}
              value={
                selectedVisualization?.id
                  ? boardOption(selectedVisualization)
                  : undefined
              }
              formatOptionLabel={BoardOptionLabel}
              onChange={(option) => {
                const selected = visualizations.find(
                  (v) => v?.id?.toString() === option?.value
                );
                setSelectedVisualization(selected || undefined);
              }}
              placeholder="Select a visualization..."
            />
          </div>
          <div className="flex gap-1">
            {selectedVisualization && !selectedVisualization.built_in && (
              <>
                <EditVisualizationDialog
                  visualization={selectedVisualization}
                  onUpdate={handleVisualizationUpdate}
                />
                <DeleteVisualizationDialog
                  visualization={selectedVisualization}
                  onDelete={handleVisualizationDelete}
                />
              </>
            )}
          </div>
        </div>
        <div className="flex gap-2">
          <CreateVisualizationDialog
            orgId={orgId}
            onVisualizationCreated={handleVisualizationCreated}
          />
        </div>
      </div>

      {selectedVisualization && selectedVisualization.columns?.length === 0 && (
        <div
          className="mx-auto w-full"
          style={{
            display: "flex",
            backgroundColor: "#f8f8f8",
            borderRadius: "0.75rem",
            alignItems: "center",
            padding: "5rem 1.5rem",
            textAlign: "center",
          }}
        >
          <div className="flex flex-col items-center justify-center w-full">
            <ErrorBlock
              status={PageStatus.ERROR}
              title="No columns"
              message={
                selectedVisualization.built_in
                  ? "Add subjects to your organization to see a column for each."
                  : "This visualization has no columns yet."
              }
            />
            {!selectedVisualization.built_in && (
              <div className="mt-4">
                <Dialog
                  open={addColumnDialogOpen}
                  onOpenChange={setAddColumnDialogOpen}
                >
                  <DialogTrigger asChild>
                    <Button color={ButtonColor.PURPLE}>Add Column</Button>
                  </DialogTrigger>
                  <AddOrEditColumnDialog
                    visualization={selectedVisualization}
                    onColumnUpdated={handleColumnUpdated}
                    onColumnAdded={handleColumnAdded}
                    studentKeys={studentKeys}
                    parentKeys={parentKeys}
                    tutorKeys={tutorKeys}
                    subjects={subjects}
                    forms={forms}
                    setIsOpen={setAddColumnDialogOpen}
                  />
                </Dialog>
              </div>
            )}
          </div>
        </div>
      )}

      {selectedVisualization && selectedVisualization.columns?.length > 0 && (
        <DndContext
          sensors={sensors}
          collisionDetection={collisionDetection}
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
          onDragCancel={() => setDragging(null)}
        >
          <div className="visualization-columns">
            {[...selectedVisualization.columns]
              .sort((a, b) => a.order_index - b.order_index)
              .map((column, index) => (
                <DroppableColumn
                  key={column.id}
                  data={{
                    columnId: column.id,
                    columnType: column.column_type,
                  }}
                  draggingType={dragging?.personType ?? null}
                  draggingFrom={dragging?.fromColumnId ?? null}
                  className={`visualization-column ${
                    index % 2 === 0 ? "bg-purple-50" : "bg-blue-50"
                  }`}
                >
                  <div className="column-header board-column-header bg-purple-600">
                    <h3 className="board-column-title text-white font-bold">
                      <span
                        className="board-column-name"
                        style={{ minHeight: namePx }}
                      >
                        {/* Measured at its own height (not the shared one). */}
                        <span
                          ref={(el) => {
                            if (el) titles.current.set(column.id, el);
                            else titles.current.delete(column.id);
                          }}
                          className="block"
                        >
                          {column.name}
                        </span>
                      </span>
                      {/* Only this button opens the column's actions. */}
                      <ColumnActionsMenu
                        column={column}
                        visualization={selectedVisualization}
                        onColumnUpdated={handleColumnUpdated}
                        onColumnAdded={handleColumnAdded}
                        onColumnDeleted={handleColumnDeleted}
                        studentKeys={studentKeys}
                        parentKeys={parentKeys}
                        tutorKeys={tutorKeys}
                        subjects={subjects}
                        forms={forms}
                        orgId={orgId}
                        builtIn={selectedVisualization.built_in}
                      />
                    </h3>
                  </div>

                  <div className="column-content">
                    {column.students_in_column?.map((student) =>
                      renderCard(student, Auth0AccountRole.ME, column)
                    )}
                    {column.parents_in_column?.map((parent) =>
                      renderCard(
                        parent as BoardPerson,
                        Auth0AccountRole.PARENT,
                        column
                      )
                    )}
                    {column.tutors_in_column?.map((tutor) =>
                      renderCard(tutor, Auth0AccountRole.ORG_TUTOR, column)
                    )}
                  </div>
                </DroppableColumn>
              ))}

            {!selectedVisualization.built_in && (
              <div className="visualization-column bg-gray-50 flex items-center justify-center">
                <Dialog
                  open={addColumnDialogOpen}
                  onOpenChange={setAddColumnDialogOpen}
                >
                  <DialogTrigger asChild>
                    <div className="w-full">
                      <div className="column-header cursor-pointer">
                        {/* Built like a column's header, so it is as tall. */}
                        <h3 className="board-column-title text-white font-bold bg-blue-400 hover:bg-blue-300 rounded-t transition-colors">
                          <span
                            className="board-column-name"
                            style={{ minHeight: namePx }}
                          >
                            Add Column
                          </span>
                          <span
                            className="board-column-actions invisible"
                            aria-hidden
                          >
                            Actions
                            <LuChevronDown strokeWidth={2.5} />
                          </span>
                        </h3>
                      </div>
                      <div className="column-content">
                        <div className="flex items-center justify-center mt-20">
                          <Button
                            shape={ButtonShape.CIRCLE}
                            color={ButtonColor.DARK_BLUE}
                          >
                            <span className="mx-2 font-bold text-xl">+</span>
                          </Button>
                        </div>
                      </div>
                    </div>
                  </DialogTrigger>

                  <AddOrEditColumnDialog
                    visualization={selectedVisualization}
                    onColumnAdded={handleColumnAdded}
                    onColumnUpdated={handleColumnUpdated}
                    setIsOpen={setAddColumnDialogOpen}
                    studentKeys={studentKeys}
                    parentKeys={parentKeys}
                    tutorKeys={tutorKeys}
                    subjects={subjects}
                    forms={forms}
                  />
                </Dialog>
              </div>
            )}
          </div>

          <DragOverlay zIndex={45} dropAnimation={null}>
            {dragging ? (
              <div className="visualization-card--overlay">
                <Tag
                  item={dragging.label}
                  color={CARD_COLORS[dragging.personType]}
                  size={TagSize.MEDIUM}
                />
              </div>
            ) : null}
          </DragOverlay>
        </DndContext>
      )}

      {chips.chipWindow}
      {cardTask.popout}

      <Dialog
        open={moveAsk !== null}
        onOpenChange={(open) => {
          if (!open) setMoveAsk(null);
        }}
      >
        {moveAsk && (
          <MoveFieldsDialog
            key={`${moveAsk.column.id}:${moveAsk.personType}:${moveAsk.personId}`}
            ask={moveAsk}
            onSaved={(columns, inColumn, message) => {
              applyColumns(moveAsk.visualizationId, columns);
              setMoveAsk(null);
              if (!inColumn && message) {
                Notifications.info(message);
              }
            }}
          />
        )}
      </Dialog>
    </div>
  );
}
